import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, HelpCircle, Check, X, Flag, FileText, MessageSquare,
  CheckSquare, Users, Briefcase, Link2, AlertCircle, History, Image as ImageIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { CollapsibleCard, SectionBadge } from '../components/ui/CollapsibleCard';
import { ETAPA_MAP, APROVACOES, PRIORIDADES } from '../lib/constants';
import { PrioridadeBadge } from '../components/demandas/PrioridadeBadge';
import { DetalhesCompactos } from '../components/demandas/DetalhesCompactos';
import { EditarDatasAcao } from '../components/demandas/EditarDatasAcao';
import { ResultadosSection } from '../components/demandas/ResultadosSection';
import { AprovacoesSection } from '../components/demandas/AprovacoesSection';
import { ParceirosSection } from '../components/demandas/ParceirosSection';
import { PedidosInfoSection } from '../components/demandas/PedidosInfoSection';
import { TarefasSection } from '../components/demandas/TarefasSection';
import { FunilDemanda } from '../components/demandas/FunilDemanda';
import { AnexosSection } from '../components/demandas/AnexosSection';

const ETAPA_ORDER = ['nova_demanda', 'aprovada', 'em_captacao', 'em_edicao', 'aguardando_aprovacao', 'pronta_para_publicar', 'publicado'];

export function DemandaDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novaMsg, setNovaMsg] = useState('');
  const [tipoMsg, setTipoMsg] = useState('nota');
  const [sending, setSending] = useState(false);
  const [usuariosEquipe, setUsuariosEquipe] = useState([]);
  const [counts, setCounts] = useState({
    tarefas: 0, tarefasAbertas: 0, pedidos: 0, pedidosAbertos: 0,
    aprovacoes: 0, aprovacoesPendentes: 0, parceiros: 0, resultados: 0,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/demandas/${id}`);
      setData(res);
      api.post(`/demandas/${id}/mensagens/lidas`).catch(() => {});
      Promise.all([
        api.get(`/demandas/${id}/tarefas`).catch(() => ({ tarefas: [] })),
        api.get(`/demandas/${id}/pedidos-info`).catch(() => ({ pedidos: [] })),
        api.get(`/demandas/${id}/aprovacoes`).catch(() => ({ aprovacoes: [] })),
        api.get(`/demandas/${id}/parceiros`).catch(() => ({ parceiros: [] })),
      ]).then(([t, p, a, pa]) => {
        setCounts({
          tarefas: t.tarefas.length,
          tarefasAbertas: t.tarefas.filter((x) => x.status !== 'concluida').length,
          pedidos: p.pedidos.length,
          pedidosAbertos: p.pedidos.filter((x) => !x.resolvido_em && !x.cancelado_em).length,
          aprovacoes: a.aprovacoes.length,
          aprovacoesPendentes: a.aprovacoes.filter((x) => x.status === 'pendente').length,
          parceiros: pa.parceiros.length,
          resultados: res.demanda.resultados?.length || 0,
        });
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isComunicacao = user?.perfil === 'comunicacao' || user?.perfil === 'admin';

  useEffect(() => {
    if (isComunicacao) {
      api.get('/usuarios')
        .then((r) => setUsuariosEquipe(r.usuarios.filter((u) => u.perfil !== 'secretario' && u.ativo)))
        .catch(() => {});
    }
  }, [isComunicacao]);

  const patchDemanda = async (patch) => {
    await api.patch(`/demandas/${id}`, patch);
    await load();
  };

  const enviarMensagem = async (e) => {
    e.preventDefault();
    if (!novaMsg.trim()) return;
    setSending(true);
    try {
      await api.post(`/demandas/${id}/mensagens`, { texto: novaMsg, tipo: tipoMsg });
      setNovaMsg('');
      setTipoMsg('nota');
      await load();
    } finally { setSending(false); }
  };

  if (loading) return <div className="loader-full"><span className="spinner" /></div>;
  if (!data) return <div className="page">Demanda não encontrada.</div>;

  const { demanda, mensagens, historico, anexos = [] } = data;
  const etapa = ETAPA_MAP[demanda.etapa];
  const etapaIdx = ETAPA_ORDER.indexOf(demanda.etapa);

  const mostrarAprovacoes = counts.aprovacoes > 0 || etapaIdx >= ETAPA_ORDER.indexOf('em_edicao');
  const mostrarResultados = counts.resultados > 0 || demanda.etapa === 'publicado' || demanda.etapa === 'pronta_para_publicar';
  const mostrarTarefas = isComunicacao || counts.tarefas > 0;
  const mostrarPedidos = counts.pedidos > 0 || isComunicacao;

  return (
    <div className="page">
      <button className="btn btn-ghost" style={{ marginBottom: 10 }} onClick={() => nav(-1)}>
        <ArrowLeft size={15} /> Voltar
      </button>

      <div style={{ marginBottom: 14 }}>
        <div className="row-wrap" style={{ marginBottom: 4, gap: 6 }}>
          <Badge color={etapa?.color}>{etapa?.label}</Badge>
          <PrioridadeBadge prioridade={demanda.prioridade} />
          {demanda.aprovacao !== 'pendente' && (
            <Badge color={APROVACOES.find((a) => a.key === demanda.aprovacao)?.color}>
              {APROVACOES.find((a) => a.key === demanda.aprovacao)?.label}
            </Badge>
          )}
          <span className="text-xs muted">#{demanda.id}</span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.25 }}>{demanda.titulo}</h1>
        <div className="text-xs muted row-wrap" style={{ gap: 10 }}>
          <span><Briefcase size={11} style={{ verticalAlign: 'middle' }} /> {demanda.secretaria_nome}</span>
          <span>·</span>
          <span>solicitante: {demanda.solicitante_nome}</span>
          {demanda.responsavel_nome && (<><span>·</span><span>coord.: {demanda.responsavel_nome}</span></>)}
          <span>·</span>
          <span>criada em {format(new Date(demanda.created_at), 'dd/MM/yyyy')}</span>
        </div>
      </div>

      {/* Caso raro: a imagem subiu mas não vinculou à demanda recém-criada.
          Avisar em vez de deixar a pessoa achando que anexou. */}
      {location.state?.anexosFalharam?.length > 0 && (
        <div className="card" style={{ marginBottom: 14, borderColor: 'var(--c-danger)', borderWidth: 2 }}>
          <div className="card-body" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle size={18} color="var(--c-danger)" />
            <div className="text-sm">
              Não consegui anexar {location.state.anexosFalharam.join(', ')} nesta demanda.
              Use <strong>Imagens e arquivos</strong> abaixo para anexar de novo.
            </div>
          </div>
        </div>
      )}

      {counts.pedidosAbertos > 0 && (
        <div className="card" style={{ marginBottom: 14, borderColor: 'var(--c-warning)', borderWidth: 2 }}>
          <div className="card-body" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--c-warning-soft)' }}>
            <AlertCircle size={18} color="var(--c-warning)" />
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 13 }}>Aguardando solicitante</strong>
              <div className="text-xs" style={{ color: '#78350f' }}>
                {counts.pedidosAbertos} pedido{counts.pedidosAbertos === 1 ? '' : 's'} de informação pendente{counts.pedidosAbertos === 1 ? '' : 's'}.
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <FunilDemanda demandaId={demanda.codigo} isComunicacao={isComunicacao} isAdmin={user?.perfil === 'admin'} onChanged={load} />
      </div>

      {isComunicacao && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-body" style={{ padding: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Prioridade:</span>
              {PRIORIDADES.map((p) => (
                <button
                  key={p.key}
                  onClick={() => patchDemanda({ prioridade: p.key })}
                  className={`btn btn-sm ${demanda.prioridade === p.key ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ padding: '4px 8px', minHeight: 26, fontSize: 11 }}
                  disabled={demanda.prioridade === p.key}
                >
                  <Flag size={11} style={{ color: p.dotColor }} /> {p.label}
                </button>
              ))}
            </div>
            <div style={{ borderLeft: '1px solid var(--c-border)', height: 22 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Coord:</span>
              <select className="select" style={{ fontSize: 12, padding: '4px 8px', minHeight: 28, maxWidth: 180 }}
                value={demanda.responsavel_id || ''}
                onChange={(e) => patchDemanda({ responsavel_id: e.target.value ? parseInt(e.target.value, 10) : null })}
              >
                <option value="">—</option>
                {usuariosEquipe.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div style={{ borderLeft: '1px solid var(--c-border)', height: 22 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Aprov:</span>
              <button className={`btn btn-sm ${demanda.aprovacao === 'aprovado' ? 'btn-primary' : 'btn-ghost'}`} style={{ padding: '4px 8px', minHeight: 26, fontSize: 11 }} onClick={() => patchDemanda({ aprovacao: 'aprovado' })}>
                <Check size={11} /> Sim
              </button>
              <button className={`btn btn-sm ${demanda.aprovacao === 'reprovado' ? 'btn-primary' : 'btn-ghost'}`} style={{ padding: '4px 8px', minHeight: 26, fontSize: 11 }} onClick={() => patchDemanda({ aprovacao: 'reprovado' })}>
                <X size={11} /> Não
              </button>
              <button className={`btn btn-sm ${demanda.aprovacao === 'pendente' ? 'btn-primary' : 'btn-ghost'}`} style={{ padding: '4px 8px', minHeight: 26, fontSize: 11 }} onClick={() => patchDemanda({ aprovacao: 'pendente' })}>
                ?
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid-2" style={{ alignItems: 'start', gap: 14 }}>
        <div className="stack" style={{ gap: 12 }}>
          <EditarDatasAcao demanda={demanda} onSalvo={load} podeEditarPostagem={isComunicacao} />
          <DetalhesCompactos demanda={demanda} podeEditar={isComunicacao} onSalvo={load} />

          <CollapsibleCard
            title="Imagens e arquivos"
            icon={ImageIcon}
            subtitle={anexos.length === 0 ? 'Anexar referências, artes e fotos da demanda' : `${anexos.length} arquivo${anexos.length === 1 ? '' : 's'} anexado${anexos.length === 1 ? '' : 's'}`}
            badge={anexos.length > 0 && <SectionBadge color="info">{anexos.length}</SectionBadge>}
            defaultOpen={anexos.length > 0}
          >
            <AnexosSection
              demanda={demanda}
              anexos={anexos}
              podeEditar={true}
              onChange={(novos) => setData((d) => ({ ...d, anexos: novos }))}
            />
          </CollapsibleCard>

          {mostrarTarefas && (
            <CollapsibleCard
              title="Tarefas da produção"
              icon={CheckSquare}
              subtitle={counts.tarefas === 0 ? 'Dividir em tarefas e atribuir responsáveis' : `${counts.tarefas - counts.tarefasAbertas}/${counts.tarefas} concluídas`}
              badge={counts.tarefasAbertas > 0 && <SectionBadge color="info">{counts.tarefasAbertas} aberta{counts.tarefasAbertas === 1 ? '' : 's'}</SectionBadge>}
              defaultOpen={counts.tarefasAbertas > 0 || (isComunicacao && counts.tarefas === 0 && ['em_captacao', 'em_edicao'].includes(demanda.etapa))}
            >
              <TarefasSection demanda={demanda} podeEditar={isComunicacao} />
            </CollapsibleCard>
          )}

          {mostrarPedidos && (
            <CollapsibleCard
              title="Pedidos de informação"
              icon={HelpCircle}
              subtitle={counts.pedidosAbertos > 0 ? 'Itens pendentes com o solicitante' : counts.pedidos === 0 ? 'Pedir dados/arquivos ao solicitante' : 'Nenhum pendente'}
              badge={counts.pedidosAbertos > 0 && <SectionBadge color="warning">{counts.pedidosAbertos} aberto{counts.pedidosAbertos === 1 ? '' : 's'}</SectionBadge>}
              alert={counts.pedidosAbertos > 0}
              defaultOpen={counts.pedidosAbertos > 0}
            >
              <PedidosInfoSection demanda={demanda} podeCriar={isComunicacao} podeResponder={true} />
            </CollapsibleCard>
          )}

          <CollapsibleCard
            title="Parceiros e patrocinadores"
            icon={Users}
            subtitle={counts.parceiros === 0 ? 'Gerar link para parceiro enviar mídias' : `${counts.parceiros} cadastrado${counts.parceiros === 1 ? '' : 's'}`}
            badge={counts.parceiros > 0 && <SectionBadge color="purple">{counts.parceiros}</SectionBadge>}
            defaultOpen={counts.parceiros > 0}
          >
            <ParceirosSection demanda={demanda} podeEditar={true} />
          </CollapsibleCard>

          {mostrarAprovacoes && (
            <CollapsibleCard
              title="Aprovações públicas"
              icon={FileText}
              subtitle={counts.aprovacoes === 0 ? 'Enviar arte ao solicitante via link' : `${counts.aprovacoesPendentes} pendente${counts.aprovacoesPendentes === 1 ? '' : 's'} de ${counts.aprovacoes}`}
              badge={counts.aprovacoesPendentes > 0 && <SectionBadge color="warning">{counts.aprovacoesPendentes}</SectionBadge>}
              defaultOpen={counts.aprovacoesPendentes > 0 || demanda.etapa === 'aguardando_aprovacao'}
            >
              <AprovacoesSection demanda={demanda} podeEditar={isComunicacao} />
            </CollapsibleCard>
          )}

          {mostrarResultados && (
            <CollapsibleCard
              title="Resultados publicados"
              icon={Link2}
              subtitle={counts.resultados === 0 ? 'Adicionar links do que foi publicado' : `${counts.resultados} link${counts.resultados === 1 ? '' : 's'}`}
              badge={counts.resultados > 0 && <SectionBadge color="success">{counts.resultados}</SectionBadge>}
              defaultOpen={demanda.etapa === 'publicado' || demanda.etapa === 'pronta_para_publicar'}
            >
              <ResultadosSection demanda={demanda} podeEditar={isComunicacao} onChange={(r) => setData((d) => ({ ...d, demanda: { ...d.demanda, resultados: r } }))} />
            </CollapsibleCard>
          )}
        </div>

        <div className="stack" style={{ gap: 12 }}>
          <div className="card">
            <div className="card-header" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={15} color="var(--c-primary)" />
              <strong style={{ fontSize: 14 }}>Conversa</strong>
              <span className="text-xs muted">· {mensagens.length}</span>
            </div>
            <div className="card-body" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="chat-list" style={{ maxHeight: 500, overflowY: 'auto' }}>
                {mensagens.length === 0 && <div className="text-sm muted center" style={{ padding: 20 }}>Sem mensagens ainda.</div>}
                {mensagens.map((m) => {
                  const mine = m.autor_id === user?.id;
                  const cls = `chat-msg ${mine ? 'mine' : ''} ${m.tipo === 'pergunta' ? 'pergunta' : ''}`;
                  return (
                    <div key={m.id} className={cls}>
                      {!mine && <Avatar nome={m.autor_nome} src={m.autor_foto} size="sm" />}
                      <div className="chat-content">
                        <div className="chat-bubble">
                          {m.tipo === 'pergunta' && <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, marginBottom: 2 }}>❓ PERGUNTA</div>}
                          {m.texto}
                        </div>
                        <div className="chat-meta">
                          {m.autor_nome?.split(' ')[0]} · {format(new Date(m.created_at), 'dd/MM HH:mm')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={enviarMensagem} className="stack" style={{ gap: 6, borderTop: '1px solid var(--c-border)', paddingTop: 10 }}>
                <textarea
                  className="textarea"
                  placeholder={isComunicacao ? 'Escreva uma mensagem (marque "pergunta" para notificar o solicitante)' : 'Escreva uma resposta...'}
                  value={novaMsg}
                  onChange={(e) => setNovaMsg(e.target.value)}
                  rows={2}
                />
                <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
                  {isComunicacao ? (
                    <label className="row" style={{ gap: 4, fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={tipoMsg === 'pergunta'} onChange={(e) => setTipoMsg(e.target.checked ? 'pergunta' : 'nota')} style={{ accentColor: 'var(--c-primary)' }} />
                      <HelpCircle size={12} /> Enviar como pergunta
                    </label>
                  ) : <span />}
                  <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !novaMsg.trim()}>
                    <Send size={13} /> Enviar
                  </button>
                </div>
              </form>
            </div>
          </div>

          {historico.length > 0 && (
            <CollapsibleCard
              title="Histórico de etapas"
              icon={History}
              subtitle={`${historico.length} mudança${historico.length === 1 ? '' : 's'} · ver histórico completo`}
              defaultOpen={false}
            >
              <div className="table-wrap">
                <table className="table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr><th>Quando</th><th>Etapa</th><th>Por</th></tr>
                  </thead>
                  <tbody>
                    {historico.slice().reverse().map((h) => (
                      <tr key={h.id}>
                        <td>{format(new Date(h.created_at), 'dd/MM HH:mm')}</td>
                        <td>
                          {h.de ? `${ETAPA_MAP[h.de]?.label || h.de} → ` : ''}
                          <strong>{ETAPA_MAP[h.para]?.label || h.para}</strong>
                        </td>
                        <td>{h.usuario_nome?.split(' ')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '10px 14px', borderTop: '1px solid var(--c-border)' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => nav(`/demandas/${id}/timeline`)}>
                  <History size={13} /> Ver histórico completo (mensagens, aprovações, tarefas...)
                </button>
              </div>
            </CollapsibleCard>
          )}
        </div>
      </div>
    </div>
  );
}
